/** Phase 2 plan 02 Task 9 — billing type + lifting on the existing fulfilment page. */
import { useCallback, useEffect, useState } from 'react';
import { api } from '@/utils/api';

type BillingType = 'direct' | 'dealer_ship_customer' | 'project';
interface Readiness { billingType: BillingType; billingTypeLabel: string; source: 'set' | 'derived'; projectName: string | null; customerLifting: boolean | null; locked: boolean }
const OPTIONS: Array<[BillingType, string]> = [['direct', 'Direct bill to end customer'], ['dealer_ship_customer', 'Bill to dealer, ship to end customer'], ['project', 'Direct bill to project']];
const liftingText = (v: boolean | null) => (v === null ? 'Lifting not set' : `Lifting: ${v ? 'Yes' : 'No'}`);

export function OrderReadinessCard({ soId }: { soId: string }) {
  const [view, setView] = useState<Readiness | null>(null);
  const [type, setType] = useState<BillingType>('direct');
  const [project, setProject] = useState('');
  const [lifting, setLifting] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { const r = await api.get(`/so-fulfilment/${soId}/readiness`); const v = r.data.data as Readiness; setView(v); setType(v.billingType); setProject(v.projectName ?? ''); setLifting(v.customerLifting); } catch { setView(null); }
  }, [soId]);
  useEffect(() => { load(); }, [load]);
  if (!view) return null;
  const save = async () => {
    setError(null);
    try {
      const body: Record<string, unknown> = { billingType: type, customerLifting: lifting };
      if (type === 'project') body.projectName = project;
      const r = await api.put(`/so-fulfilment/${soId}/readiness`, body);
      setView(r.data.data);
    } catch (e: any) { setError(e?.response?.data?.error || 'Could not save the billing type.'); }
  };
  const text = [view.billingTypeLabel, view.source === 'derived' ? 'from dealer billing' : null, view.billingType === 'project' ? view.projectName : null, liftingText(view.customerLifting)].filter(Boolean).join(' · ');
  return (
    <div data-testid="order-readiness" className="mb-6 rounded-xl border p-4" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
      <p data-testid="readiness-text" className="text-sm" style={{ color: 'var(--text-primary)' }}>{text}</p>
      {!view.locked && (
        <div className="mt-3 flex flex-wrap items-end gap-3 text-xs" style={{ color: 'var(--text-muted)' }}>
          <label>Billing type
            <select aria-label="Billing type" value={type} onChange={(e) => setType(e.target.value as BillingType)} className="mt-1 block rounded-lg border px-3 py-1.5 text-sm" style={{ borderColor: 'var(--border-primary)' }}>
              {OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          {type === 'project' && (
            <label className="flex-1">Project name
              <input aria-label="Project name" value={project} onChange={(e) => setProject(e.target.value)} className="mt-1 block w-full rounded-lg border px-3 py-1.5 text-sm" style={{ borderColor: 'var(--border-primary)' }} />
            </label>
          )}
          <fieldset className="flex items-center gap-3"><legend className="sr-only">Lifting</legend>
            <label><input type="radio" name={`lifting-${soId}`} aria-label="Lifting: Yes" checked={lifting === true} onChange={() => setLifting(true)} /> Lifting: Yes</label>
            <label><input type="radio" name={`lifting-${soId}`} aria-label="Lifting: No" checked={lifting === false} onChange={() => setLifting(false)} /> Lifting: No</label>
          </fieldset>
          <button onClick={save} className="rounded-lg px-3 py-1.5 text-sm font-medium" style={{ background: 'var(--bg-muted)', color: 'var(--text-primary)' }}>Save readiness</button>
        </div>
      )}
      {error && <p className="mt-2 text-sm" style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}
